import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { GateThresholds } from './gates';
import type { OrientationLimits } from './orientation';
import { CaptureSession, type CaptureResult, type LiveState } from './session';

/** Opzioni lette a ogni controllo (non riaprono la fotocamera quando cambiano: soglie e limiti sono dati). */
export interface UseCaptureOptions {
  thresholds: GateThresholds;
  limits: OrientationLimits;
  /** A ogni controllo del ciclo (5 volte al secondo), dopo l'aggiornamento dello stato. */
  onLive?: (state: LiveState, nowMs: number) => void;
}

export interface UseCapture {
  /** Lo stato dal vivo, null finché la fotocamera non è pronta. */
  live: LiveState | null;
  /** Il motivo per cui la fotocamera non è disponibile, o null. */
  error: string | null;
  /** Esito del blocco del fuoco ('locked' o il motivo), null finché la fotocamera non è pronta. */
  focus: string | null;
  settings: MediaTrackSettings | null;
  shoot(): Promise<CaptureResult>;
}

/**
 * La sessione di cattura come hook: apre fotocamera, sensore e schermo acceso quando la vista compare e li chiude quando
 * sparisce. È l'UNICO punto in cui il recorder e la schermata operatore toccano la fotocamera (WP1).
 */
export function useCaptureSession(videoRef: RefObject<HTMLVideoElement | null>, scratchRef: RefObject<HTMLCanvasElement | null>, options: UseCaptureOptions): UseCapture {
  const latest = useRef(options);
  latest.current = options;
  const sessionRef = useRef<CaptureSession | null>(null);
  const [live, setLive] = useState<LiveState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<{ focus: string; settings: MediaTrackSettings } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const session = await CaptureSession.open(videoRef.current!, scratchRef.current!, {
          thresholds: () => latest.current.thresholds,
          limits: () => latest.current.limits,
          signal: controller.signal,
          onLive: (state, nowMs) => {
            setLive(state);
            latest.current.onLive?.(state, nowMs);
          },
        });
        // La vista può essere sparita mentre la fotocamera si apriva (StrictMode, cambio di fase): niente resta acceso.
        if (controller.signal.aborted) {
          session.close();
          return;
        }
        sessionRef.current = session;
        setInfo({ focus: session.focus, settings: session.settings });
      } catch (e) {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      controller.abort();
      sessionRef.current?.close();
      sessionRef.current = null;
    };
  }, [videoRef, scratchRef]);

  const shoot = useCallback((): Promise<CaptureResult> => {
    const session = sessionRef.current;
    if (!session) return Promise.reject(new Error('Fotocamera non pronta'));
    return session.shoot();
  }, []);

  return { live, error, focus: info?.focus ?? null, settings: info?.settings ?? null, shoot };
}
